import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Check, Sparkles, Clock, AlertTriangle, FileText, ReceiptText, Hourglass } from 'lucide-react';
import {
  getSubscriptionStatus, getSubscriptionPackages, getDoctorPayments,
} from '../redux/subscriptionSlice';
import { ACCESS_CONTROLS, BILLING_PERIODS, PACKAGE_ACCENTS } from '../data/accessControls';
import SubscriptionRequestModal from '../components/modals/SubscriptionRequestModal';
import PackageCardView from '../components/subscription/PackageCardView';
import InvoiceModal from '../components/subscription/InvoiceModal';
import ContactModal from '../components/subscription/ContactModal';
import { subscriptionPrice } from '../utils/currency';
import BackToOrigin from '../components/common/BackToOrigin';

const STATUS_BADGE = {
  trial: { label: 'Trial', cls: 'bg-blue-100 text-blue-700' },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  grace: { label: 'Grace period', cls: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700' },
};

const priceLabel = (p) => {
  if (!p || p.price == null || String(p.price) === '') return 'On request';
  return String(p.price) === '0' ? 'Free' : subscriptionPrice(p.price, p);
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Payment rows are no longer all paid: a plan request writes a pending invoice
// row, and a declined request cancels it.
const statusCls = (st) => (
  String(st) === 'pending' ? 'bg-amber-100 text-amber-700'
    : String(st) === 'cancelled' ? 'bg-gray-100 text-gray-500'
      : 'bg-emerald-100 text-emerald-700'
);

// Lowest enabled period price → "from <sign>X"; falls back to the legacy single price.
// Every sign on this page comes from the row's own currency via
// utils/currency.js (P24) — see SUBSCRIPTION_CURRENCY there.
// A custom (non-purchasable) plan shows "Custom pricing" instead of a number.
const cardPrice = (p) => {
  if (p.purchasable === false) return 'Custom pricing';
  const bp = p.billingPeriods || {};
  const prices = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled)
    .map((x) => Number(bp[x.id].price)).filter((n) => Number.isFinite(n));
  return prices.length ? `from ${subscriptionPrice(Math.min(...prices), p)}` : priceLabel(p);
};
const periodHint = (p) => {
  const bp = p.billingPeriods || {};
  const labels = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled).map((x) => x.label);
  return labels.length ? labels.join(' · ') : `${p.durationDays || 30} days`;
};

const MySubscription = () => {
  const dispatch = useDispatch();
  // WHOSE SUBSCRIPTION IS ON SCREEN (owner, 16 Sep 2026).
  // A doctor sees their own, exactly as before. A tenant ADMIN may open ONE
  // doctor's plan by ?doctorId= (the link on that doctor's profile) and it is
  // READ-ONLY: the plan, what it includes and the payment history, with no
  // checkout, no renew and no "contact us" — buying is the doctor's own act and
  // the server prices and records it against their id.
  const [searchParams] = useSearchParams();
  const viewerRole = (localStorage.getItem('Role') || '').toLowerCase();
  const readOnly = viewerRole === 'admin';
  const doctorId = readOnly ? (searchParams.get('doctorId') || '') : localStorage.getItem('ID');
  // An admin who arrived without naming a doctor: there is no hospital-wide
  // subscription to show, so the page asks for one rather than inventing one.
  const awaitingDoctorChoice = readOnly && !doctorId;
  const { mySubscription: sub, statusLoading, packages, payments, loading } = useSelector((s) => s.subscription);
  const [requestPkg, setRequestPkg] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [contactPkg, setContactPkg] = useState(null);

  useEffect(() => {
    if (doctorId) {
      dispatch(getSubscriptionStatus(doctorId));
      dispatch(getDoctorPayments(doctorId));
    }
    // Always refetch so the plan cards + checkout modal have current billing
    // periods/prices (a stale list would send no period and 400 at checkout).
    dispatch(getSubscriptionPackages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const badge = STATUS_BADGE[sub?.status] || STATUS_BADGE.active;
  const expired = sub?.status === 'expired';

  const included = ACCESS_CONTROLS.filter((c) => {
    const v = sub?.controls?.[c.id];
    return v !== undefined && v !== false && v !== '';
  }).map((c) => ({ id: c.id, label: c.label, value: c.type === 'value' ? String(sub.controls[c.id]) : null }));

  // Plans to switch/upgrade to = active, non-default, and NOT the current plan
  // (you renew the current one from the Current plan card, not this list).
  const buyable = packages.filter((p) => p.isActive !== false && !p.isDefault && p.id !== sub?.packageId);

  // The current plan's package (for the Renew button). Renew is offered only for a
  // renewable paid plan in its last stretch (<= 10 days left) — not the Free trial.
  const currentPkg = packages.find((p) => p.id === sub?.packageId) || null;
  const canRenew = !readOnly && !!currentPkg && currentPkg.purchasable !== false && !sub?.isFree && (sub?.daysLeft ?? 99) <= 10;

  // WHO PUT THE PENDING CHANGE THERE (owner, 22 Sep 2026 — Q8). Two very
  // different situations wear the same `pending` block, and the server now says
  // which with `pendingSource` (mirrored as `pending.source`):
  //   'admin'  — an administrator assigned a plan the doctor must pay for. It
  //              sets accessAllowed=false and locks the app until it is
  //              settled, so it keeps the loud banner.
  //   'doctor' — the doctor asked for this plan themselves. Nothing is wrong
  //              and nothing is locked (accessAllowed stays true); an invoice
  //              is on its way. A red "action required" bar for your own
  //              request would be a lie.
  // `paymentRequired` is true for BOTH, so it cannot be the discriminator on
  // its own. A row from before the column existed carries no source; it can
  // only be an admin assignment, which is what the old screen assumed.
  const pending = sub?.pending || null;
  const pendingSource = sub?.pendingSource || pending?.source || (pending ? 'admin' : null);
  const pendingPay = (sub?.paymentRequired && pending && pendingSource !== 'doctor') ? pending : null;
  const requestPending = pendingSource === 'doctor' ? pending : null;

  const refresh = () => {
    if (doctorId) {
      dispatch(getSubscriptionStatus(doctorId));
      dispatch(getDoctorPayments(doctorId));
    }
  };

  return (
    <div className="w-full" data-testid="my-subscription-page">
      <BackToOrigin />
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-1">
        {readOnly ? 'Doctor subscription' : 'My Subscription'}
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        {readOnly
          ? "This doctor's current plan, what's included, and their payment history."
          : "Your current plan, what's included, and how to renew or upgrade."}
      </p>

      {awaitingDoctorChoice ? (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center" data-testid="subscription-pick-doctor">
          <p className="text-sm text-slate-500">
            Open a doctor&apos;s profile and use <strong>Subscription</strong> to see their plan.
          </p>
        </div>
      ) : statusLoading && !sub ? (
        <div className="py-10 flex justify-center">
          <div className="h-7 w-7 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="my-subscription-content">
          {/* 0. Compulsory payment — admin set a new plan that must be paid to activate */}
          {pendingPay && (
            <div className="bg-gradient-to-r from-[#4ABDBD]/10 to-[#3A9B9B]/10 border-2 border-[#4ABDBD] rounded-2xl p-5 sm:p-6" data-testid="pending-payment-card">
              <div className="flex items-start gap-3 flex-wrap">
                <span className="p-2.5 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white shrink-0"><CreditCard className="w-5 h-5" /></span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-[#1a365d]">Action required — this plan must be paid to activate</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Your administrator set you to <strong>{pendingPay.packageName}</strong>{pendingPay.periodLabel ? ` (${pendingPay.periodLabel})` : ''}. You can&apos;t continue until this is paid.
                  </p>
                  <div className="mt-3 inline-flex flex-col gap-1 text-sm">
                    <div className="flex items-center justify-between gap-8"><span className="text-slate-500">Plan price</span><span className="font-semibold text-slate-700">{subscriptionPrice(pendingPay.amount, pendingPay, { grouped: true })}</span></div>
                    {pendingPay.credit > 0 && (
                      <div className="flex items-center justify-between gap-8 text-emerald-600"><span>Credit · unused days</span><span>−{subscriptionPrice(pendingPay.credit, pendingPay, { grouped: true })}</span></div>
                    )}
                    <div className="flex items-center justify-between gap-8 pt-1 border-t border-[#4ABDBD]/30"><span className="font-semibold text-[#1a365d]">Total due</span><span className="font-bold text-[#1a365d]">{subscriptionPrice(pendingPay.due, pendingPay, { grouped: true })}</span></div>
                  </div>
                </div>
                {/* No Pay button any more: there is no gateway to pay through.
                    What this doctor needs is the invoice and the fact that an
                    administrator switches the plan on once it is settled — so
                    the corner states that instead of offering a dead action. */}
                <div hidden={readOnly} data-testid="pending-invoice-note" className="shrink-0 max-w-[15rem] rounded-xl bg-white/70 border border-[#4ABDBD]/40 px-4 py-3">
                  <p className="text-sm font-semibold text-[#1a365d] flex items-center gap-2"><ReceiptText className="w-4 h-4" /> Invoice on its way</p>
                  <p className="text-xs text-slate-600 mt-1">
                    Pay by UPI or bank transfer using the invoice reference. Your administrator activates the plan once the payment is confirmed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 0b. The doctor's OWN request — calm, because nothing is wrong. */}
          {requestPending && !readOnly && (
            <div className="bg-[#4ABDBD]/5 border border-[#4ABDBD]/40 rounded-2xl p-5 sm:p-6" data-testid="pending-request-card">
              <div className="flex items-start gap-3 flex-wrap">
                <span className="p-2.5 rounded-xl bg-[#4ABDBD]/15 text-[#2D8A8A] shrink-0"><Hourglass className="w-5 h-5" /></span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-[#1a365d]">Request received — invoice on its way</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    You asked for <strong>{requestPending.packageName}</strong>{requestPending.periodLabel ? ` (${requestPending.periodLabel})` : ''}. Your plan activates once the payment is confirmed — nothing changes until then.
                  </p>
                  <div className="mt-3 inline-flex flex-col gap-1 text-sm">
                    <div className="flex items-center justify-between gap-8"><span className="text-slate-500">Plan price</span><span className="font-semibold text-slate-700">{subscriptionPrice(requestPending.amount, requestPending, { grouped: true })}</span></div>
                    {requestPending.credit > 0 && (
                      <div className="flex items-center justify-between gap-8 text-emerald-600"><span>Credit · unused days</span><span>−{subscriptionPrice(requestPending.credit, requestPending, { grouped: true })}</span></div>
                    )}
                    <div className="flex items-center justify-between gap-8 pt-1 border-t border-[#4ABDBD]/30"><span className="font-semibold text-[#1a365d]">To invoice</span><span className="font-bold text-[#1a365d]">{subscriptionPrice(requestPending.due, requestPending, { grouped: true })}</span></div>
                  </div>
                </div>
                {/* No "cancel" button: POST …/decline is admin-only on the
                    server, so a doctor pressing it would only collect a 403.
                    The reference is what they need here — it is what goes on
                    the UPI or bank transfer. */}
                {requestPending.reference && (
                  <div data-testid="request-reference" className="shrink-0 rounded-xl bg-white/70 border border-[#4ABDBD]/40 px-4 py-3">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Reference</p>
                    <p className="text-sm font-bold text-[#1a365d] font-mono mt-0.5">{requestPending.reference}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 1. Current plan */}
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6" data-testid="current-plan-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white">
                  <CreditCard className="w-4 h-4" />
                </span>
                <h3 className="text-base sm:text-lg font-bold text-[#1a365d]">Current plan</h3>
              </div>
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {sub?.packageName || '—'}
                  </span>
                  <span className="text-sm text-slate-500">{priceLabel(sub)}</span>
                </div>

                {/* Days left / expiry line */}
                <div className={`flex items-center gap-2 text-sm rounded-xl px-3 py-2 ${expired ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'}`}>
                  {expired ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  {expired
                    ? <span>Expired on {fmtDate(sub?.endDate)} — renew to continue.</span>
                    : <span><strong>{sub?.daysLeft ?? 0}</strong> day{(sub?.daysLeft === 1) ? '' : 's'} left · ends {fmtDate(sub?.graceUntil && sub?.status === 'grace' ? sub.graceUntil : sub?.endDate)}</span>}
                </div>

                {/* Upgrade credit hint */}
                {Number(sub?.remainingValue) > 0 && (
                  <p className="text-xs text-emerald-600 mt-2" data-testid="remaining-value-hint">
                    {subscriptionPrice(sub.remainingValue, sub, { grouped: true })} unused value — credited toward your next plan if you upgrade now.
                  </p>
                )}

                {/* Renew — only in the last stretch of a paid plan (<= 10 days left) */}
                {canRenew && (
                  <button
                    type="button"
                    onClick={() => setRequestPkg(currentPkg)}
                    data-testid="current-plan-renew"
                    className="mt-3 inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                  >
                    Request a renewal
                  </button>
                )}
              </div>

              <div>
                {included.length > 0 ? (
                  <>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#4ABDBD]" /> What's included
                    </p>
                    <ul className="space-y-1.5">
                      {included.map((f) => (
                        <li key={f.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{f.label}{f.value ? `: ${f.value}` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">No plan features available.</p>
                )}
              </div>
            </div>
          </div>

          {/* 2. Plans to choose / upgrade / renew — hidden while ANY change is
              pending: an admin assignment the doctor owes, or their own open
              request (the server allows one at a time and would refuse a second
              with REQUEST_PENDING). */}
          {!pendingPay && !requestPending && !readOnly && (
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#1a365d] mb-1">
              {expired ? 'Renew or upgrade to continue' : 'Switch or renew your plan'}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {sub?.freeUsed
                ? 'Your free trial has been used — request a paid plan to continue.'
                : 'Ask for a paid plan whenever you’re ready to upgrade.'}
              {' '}No card is needed: we e-mail you an invoice and the plan starts once the payment is confirmed.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {buyable.map((p) => {
                const isCurrent = p.id === sub?.packageId;
                const isCustom = p.purchasable === false;
                const a = PACKAGE_ACCENTS[p.accent] || PACKAGE_ACCENTS.teal;
                const renderPrice = (onAccent) => (
                  <div className="mt-1">
                    <span className={`${isCustom ? 'text-xl' : 'text-2xl'} font-black ${onAccent ? 'text-white' : a.text}`}>{cardPrice(p)}</span>
                    <p className={`text-xs mt-0.5 ${onAccent ? 'text-white/80' : 'text-slate-400'}`}>
                      {isCustom ? 'Tailored to your needs' : periodHint(p)}
                    </p>
                  </div>
                );
                return (
                  <PackageCardView
                    key={p.id}
                    pkg={p}
                    cardTestId={`plan-${p.name}`}
                    highlight={isCurrent}
                    renderPrice={renderPrice}
                    footer={
                      isCustom ? (
                        <button
                          type="button"
                          onClick={() => setContactPkg(p)}
                          data-testid={`contact-${p.name}`}
                          className="w-full px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                        >
                          Contact us
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setRequestPkg(p)}
                          data-testid={`choose-${p.name}`}
                          className={`w-full px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                            isCurrent
                              ? 'bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20'
                              : 'text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg'
                          }`}
                        >
                          {isCurrent ? 'Request a renewal' : 'Request this plan'}
                        </button>
                      )
                    }
                  />
                );
              })}
            </div>

            {loading && buyable.length === 0 && (
              <div className="py-8 flex justify-center" data-testid="plans-loading">
                <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!loading && buyable.length === 0 && (
              <p className="text-sm text-slate-500">No plans available right now.</p>
            )}
          </div>
          )}

          {/* 3. Payment history + invoices (bottom) */}
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6" data-testid="payment-history">
            <h3 className="text-base sm:text-lg font-bold text-[#1a365d] mb-3">Payment history</h3>
            {payments?.length > 0 ? (
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                      <th className="py-2 pr-3 font-semibold">Date</th>
                      <th className="py-2 pr-3 font-semibold">Plan</th>
                      <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                      <th className="py-2 pr-3 font-semibold">Status</th>
                      <th className="py-2 font-semibold text-right">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                        <td className="py-2.5 pr-3 text-gray-600">{fmtDate(p.createdAt)}</td>
                        <td className="py-2.5 pr-3 text-gray-700 whitespace-normal">{p.packageName}</td>
                        <td className="py-2.5 pr-3 text-right font-semibold text-gray-800">
                          {p.amount && String(p.amount) !== '' ? subscriptionPrice(p.amount, p) : '—'}
                          {Number(p.creditApplied) > 0 && (
                            <span className="block text-[10px] font-normal text-emerald-600">−{subscriptionPrice(p.creditApplied, p)} credit</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3">
                          {/* A request now writes a PENDING invoice row before any
                              money moves, so the history is no longer all-paid. */}
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusCls(p.status)}`}>
                            {String(p.status || 'paid').replace(/^\w/, (c) => c.toUpperCase())}
                          </span>
                          {p.reference && String(p.status) === 'pending' && (
                            <span className="block text-[10px] text-slate-400 font-mono mt-0.5">{p.reference}</span>
                          )}
                        </td>
                        <td className="py-2.5 text-right">
                          {String(p.status || 'paid') === 'cancelled' ? (
                            <span className="text-slate-300 text-xs">—</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedInvoice(p)}
                              data-testid={`view-invoice-${p.id}`}
                              className="inline-flex items-center gap-1 text-[#4ABDBD] hover:text-[#2D8A8A] font-semibold"
                            >
                              <FileText className="w-3.5 h-3.5" /> View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No payments yet.</p>
            )}
          </div>
        </div>
      )}

      {requestPkg && (
        <SubscriptionRequestModal
          pkg={requestPkg}
          doctorId={doctorId}
          onClose={() => setRequestPkg(null)}
          onSuccess={refresh}
        />
      )}

      {selectedInvoice && (
        <InvoiceModal
          payment={selectedInvoice}
          doctorId={doctorId}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {contactPkg && (
        <ContactModal
          pkg={contactPkg}
          source="doctor"
          doctorId={doctorId}
          onClose={() => setContactPkg(null)}
        />
      )}
    </div>
  );
};

export default MySubscription;
